import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CloseIcon, SendIcon } from './icons.jsx'
import ApolloSendDialog from './ApolloSendDialog.jsx'
import {
  cancelApolloSend,
  isPending,
  loadApolloSendsOnce,
  sendForDraft,
  statusLabel,
  subscribeApolloSends,
} from '../lib/apolloSend.js'
import { showToast } from '../lib/toast.js'

// The Apollo half of a draft card (Chat.jsx LeadCard / CustomerCard): a
// "Send via Apollo" button beside the copy buttons, the send's state as a chip
// once it exists (Scheduled · Tue 9:00 AM → Sent via Apollo · 2 opens, or the
// reason it was not sent), and Cancel while a scheduled send is still waiting.
//
// A card hands over the email it is SHOWING — the recipient it is addressed
// to, the subject and the signed body — plus the unsigned body, so the dialog
// can leave our signature off when the Apollo mailbox adds its own. `draftKey`
// is the card's per-draft key (the same one its Copied mark uses): the server
// hashes it into the send id, so the same draft can never go twice.
//
// <ApolloBatch> around a stack of cards adds one "Send all via Apollo" bar for
// every card inside it that has an email and has not gone yet. Cards register
// through context; nothing is threaded through props.

const BatchContext = createContext(null)

export function useApolloSend(draftKey) {
  const [send, setSend] = useState(() => sendForDraft(draftKey))
  useEffect(() => {
    setSend(sendForDraft(draftKey))
    loadApolloSendsOnce()
    return subscribeApolloSends(() => setSend(sendForDraft(draftKey)))
  }, [draftKey])
  return send
}

const goneOrGoing = (send) => Boolean(send && (isPending(send) || send.status === 'sent'))

const CHIP_TONE = {
  sent: 'border-[rgba(22,163,74,0.35)] text-good',
  failed: 'border-[rgba(220,38,38,0.35)] text-danger',
  canceled: 'border-line text-faint',
}

export default function ApolloSendControls({ email, draftKey, disabled = false, buttonClassName = '' }) {
  const batch = useContext(BatchContext)
  const send = useApolloSend(draftKey)
  const [open, setOpen] = useState(false)
  const [canceling, setCanceling] = useState(false)

  // Register with the surrounding batch (if any) — only an email that exists
  // and has not already gone or been queued.
  // The update and the unregister are separate effects on purpose: a cleanup
  // that cleared the entry on every re-render would bump the bar, which
  // re-renders the cards, which would clear it again.
  const sendable = Boolean(email && email.to && !disabled)
  useEffect(() => {
    if (!batch || !draftKey) return
    batch.set(draftKey, sendable && !goneOrGoing(send) ? { ...email, draft_key: draftKey } : null)
  }, [batch, draftKey, sendable, send, email])
  useEffect(() => {
    if (!batch || !draftKey) return undefined
    return () => batch.set(draftKey, null)
  }, [batch, draftKey])

  async function cancel() {
    if (!send || canceling) return
    setCanceling(true)
    try {
      await cancelApolloSend(send.id)
      showToast({ tone: 'success', title: 'Canceled', body: `The email to ${send.to} won’t be sent.` })
    } catch (err) {
      showToast({ tone: 'warn', title: 'Couldn’t cancel', body: err?.message || 'Try again.' })
    } finally {
      setCanceling(false)
    }
  }

  const chip = send ? statusLabel(send) : ''
  const tone = CHIP_TONE[send?.status] || 'border-line text-dim'

  return (
    <>
      {!goneOrGoing(send) && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!sendable}
          title={email?.to ? `Send this email to ${email.to} through Apollo, now or at a time you pick` : 'No recipient address on this card'}
          className={buttonClassName}
        >
          <SendIcon className="h-[14px] w-[14px]" />
          {send?.status === 'failed' || send?.status === 'canceled' ? 'Send again via Apollo' : 'Send via Apollo'}
        </button>
      )}
      {chip && (
        <span
          className={`inline-flex max-w-full items-center gap-x-1.5 rounded-full border bg-surface px-2.5 py-[3px] text-[12px] font-medium ${tone}`}
          title={send?.from ? `From ${send.from}` : undefined}
        >
          <span className="truncate">{chip}</span>
          {send?.status === 'scheduled' && send.scheduled && (
            <button
              type="button"
              onClick={cancel}
              disabled={canceling}
              aria-label="Cancel this scheduled email"
              className="grid h-4 w-4 place-items-center rounded-full text-faint hover:text-ink disabled:opacity-50"
            >
              <CloseIcon className="h-[10px] w-[10px]" />
            </button>
          )}
        </span>
      )}
      {open && (
        <ApolloSendDialog
          emails={[{ ...email, draft_key: draftKey }]}
          title={`Send to ${email?.to_name || email?.to || 'this lead'} via Apollo`}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

/** One "Send all via Apollo" bar over every registered card inside. */
export function ApolloBatch({ children, noun = 'emails', className = '' }) {
  const registry = useRef(new Map())
  const [version, setVersion] = useState(0)
  const [open, setOpen] = useState(false)
  const set = useCallback((key, email) => {
    const had = registry.current.get(key)
    if (email) registry.current.set(key, email)
    else registry.current.delete(key)
    // Repaint the bar only when what it would send changed — not on every
    // card re-render that hands over an equal email in a new object.
    const same = had && email && had.to === email.to && had.subject === email.subject && had.body_text === email.body_text
    if (!same && (had || email)) setVersion((v) => v + 1)
  }, [])
  const ctx = useMemo(() => ({ set }), [set])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const emails = useMemo(() => [...registry.current.values()], [version])

  return (
    <BatchContext.Provider value={ctx}>
      {emails.length >= 2 && (
        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-line bg-surface px-3.5 py-2.5 ${className}`}>
          <span className="text-[13px] text-dim">
            <span className="font-semibold text-ink">{emails.length}</span> {noun} ready to send
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-x-1.5 rounded-[9px] bg-primary px-[12px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark"
          >
            <SendIcon className="h-[14px] w-[14px]" />
            Send all via Apollo
          </button>
        </div>
      )}
      {children}
      {open && (
        <ApolloSendDialog emails={emails} title={`Send ${emails.length} ${noun} via Apollo`} onClose={() => setOpen(false)} />
      )}
    </BatchContext.Provider>
  )
}
